import React, {
  ComponentType,
  ReactNode,
  Ref,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { track } from "../../utils/tracking";
import { Icon as LucideIcon } from "../Common/Icons/LucideIcons";
import { Switch } from "../Inputs";
import {
  ButtonWrapper,
  LayerIcon,
  LayerName,
  LayerText,
  LayerWrapper,
  Legend,
  LegendInner,
  Row,
} from "./__styles__/Layer";
import { LayerGroupContext } from "./LayerGroup";
import { Group, GroupId, LayerContext } from "./layers";
import { LayerSectionContext } from "./LayerSection";

interface BaseLayerProps {
  id: string;
  name: string;
  ariaLabel?: string;
  Icon?: ComponentType;
  color?: string;
  nested?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  switchId: string;
  value: boolean;
  onToggle: (value: boolean) => void;
}

export const BaseLayer = ({
  id,
  name,
  ariaLabel,
  Icon,
  color,
  nested = false,
  children: legend,
  switchId,
  value,
  onToggle,
  disabled = false,
}: BaseLayerProps) => {
  const { show: layerSectionExpanded } = useContext(LayerSectionContext) ?? {};
  const { show: layerGroupExpanded } = useContext(LayerGroupContext) ?? {};

  const layerTextRef: Ref<HTMLSpanElement> = useRef(null);
  const [layerTextOverflows, setLayerTextOverflows] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleIsExpanded = () => setIsExpanded(expanded => !expanded);

  const hotkeyRef = useHotkeys<HTMLSpanElement>(
    ["space", "return"],
    toggleIsExpanded,
    { preventDefault: true },
  );

  useLayoutEffect(() => {
    if (layerTextRef.current) {
      setLayerTextOverflows(
        layerTextRef.current.offsetWidth < layerTextRef.current.scrollWidth,
      );
    }
  }, []);

  const stopCollapsibleTrigger = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const layerTextExpandable = !!legend || layerTextOverflows;
  const hidden = layerSectionExpanded === false || layerGroupExpanded === false;

  return (
    <LayerWrapper>
      <Row
        onClick={toggleIsExpanded}
        expandable={layerTextExpandable}
        nested={nested}
      >
        <LayerName
          id={id}
          aria-controls={`${id}-layer-text`}
          tabIndex={!layerTextExpandable || hidden ? -1 : 0}
          onClick={toggleIsExpanded}
          ref={hotkeyRef}
        >
          {!!Icon && (
            <LayerIcon>
              <Icon />
            </LayerIcon>
          )}
          {!!color && (
            <LayerIcon>
              <LucideIcon
                iconName="circle"
                // component requires a color, but it is ignored since strokeWidth is 0
                color="contentPrimary"
                strokeWidth={0}
                fill={color}
                aria-label={`Visual legend icon for ${name}`}
              />
            </LayerIcon>
          )}
          <LayerText
            id={`${id}-layer-text`}
            ref={layerTextRef}
            expanded={layerTextOverflows && isExpanded}
            hasIcon={!!Icon || !!color}
            nested={nested}
            aria-label={ariaLabel ?? name}
            aria-labelledby={id}
            aria-hidden={layerTextExpandable ? hidden : undefined}
          >
            {name}
          </LayerText>
        </LayerName>
        <ButtonWrapper>
          {layerTextExpandable && (
            <LucideIcon
              iconName={isExpanded ? "chevron-up" : "chevron-down"}
              color={"contentSecondary"}
              size={12}
            />
          )}
          <span onClick={stopCollapsibleTrigger}>
            <Switch
              id={switchId}
              name={name}
              onChange={onToggle}
              value={value}
              size="tiny"
              disabled={disabled}
              tabIndex={hidden ? -1 : 0}
            />
          </span>
        </ButtonWrapper>
      </Row>
      {!!legend && (
        <Legend expanded={isExpanded} nested={nested}>
          <LegendInner data-testid={`legend-${name}`}>{legend}</LegendInner>
        </Legend>
      )}
    </LayerWrapper>
  );
};

interface LayerProps<G extends Group> {
  name: string;
  ariaLabel?: string;
  group: G;
  id: GroupId<G>;
  disabled?: boolean;
  color?: string;
  Icon?: ComponentType;
  children?: ReactNode;
  nested?: boolean;
}

const Layer = <G extends Group>({
  name,
  ariaLabel,
  group,
  id,
  disabled = false,
  color,
  Icon,
  children: legend,
  nested = false,
}: LayerProps<G>) => {
  const { toggleLayer, isLayerVisible } = useContext(LayerContext);
  const handleToggle =
    ({
      id,
      group,
    }: {
      id: LayerProps<G>["id"];
      group: LayerProps<G>["group"];
    }) =>
    (isVisible: boolean) => {
      track("Map Layers Changed", {
        layerId: id,
        layerName: name,
        layerGroup: group,
        visible: isVisible,
      });

      if (id) {
        toggleLayer({ id, group, isVisible });
      }
    };

  const switchId = `${group}-${id}-switch`;
  const currentVisibility = id ? isLayerVisible({ id, group }) : false;

  return (
    <BaseLayer
      id={id}
      name={name}
      ariaLabel={ariaLabel}
      Icon={Icon}
      color={color}
      nested={nested}
      switchId={switchId}
      value={currentVisibility}
      onToggle={handleToggle({ id, group })}
      disabled={disabled}
    >
      {legend}
    </BaseLayer>
  );
};

export default Layer;
